<template>
  <div>
    <div v-if="isSidebarOpen" class="overlay" @click="toggleSidebar"></div>

    <div class="menu-icon" @click="toggleSidebar">
      <font-awesome-icon :icon="['fas', 'bars']" v-if="!isSidebarOpen" />
    </div>

    <div class="sidebar" :class="{ 'sidebar-open': isSidebarOpen }" @click.stop>
      <img
        class="img-logo"
        :src="whitelabel?.logo || defaultLogo"
        alt="Logo"
        @click="scrollToTop"
      />

      <div class="search-bar">
        <input
          type="text"
          :placeholder="$t('general.search')"
          v-model="searchQuery"
        />
        <button @click="clearSearch">
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
      </div>

      <nav>
        <ul>
          <li>
            <a href="#main">
              <span
                class="section-title"
                :class="{ active: activeItem === introduction }"
                @click="setActive(introduction)"
              >
                {{ introduction }}
              </span>
            </a>
          </li>
          <li v-for="(item, index) in filteredItems" :key="index">
            <div v-if="item.subItems">
              <span
                class="section-title"
                :class="{ active: isActive(item.title, index) }"
                @click="handleMenuClick(item, index)"
              >
                {{ item.title }}
                <font-awesome-icon
                  :icon="
                    isSubmenuOpen(index)
                      ? ['fas', 'angle-up']
                      : ['fas', 'angle-down']
                  "
                />
              </span>
              <ul v-if="isSubmenuOpen(index)">
                <li
                  v-for="(subItem, subIndex) in item.subItems"
                  :key="subIndex"
                  @click="handleClick(subItem.url)"
                >
                  <a>{{ subItem.title }}</a>
                </li>
              </ul>
            </div>
            <div v-else>
              <a
                :href="item.url"
                @click.prevent="scrollToSection(item.url, item.title, index)"
              >
                {{ item.title }}
              </a>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Sidebar",
  data() {
    return {
      searchQuery: "",
      openSubmenus: [],
      activeIndex: null,
      activeItem: this.$t("general.introduction"),
      isSidebarOpen: false,
      apiData: null,
      defaultLogo: "./assets/logo-dark-tagline.svg",
    };
  },
  computed: {
    introduction() {
      return this.$t("general.introduction");
    },
    filteredItems() {
      if (this.searchQuery) {
        return this.items
          .map((item) => {
            const filteredSubItems = item.subItems.filter((subItem) =>
              subItem.title
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase())
            );
            return {
              title: item.title,
              subItems: filteredSubItems.length ? filteredSubItems : null,
            };
          })
          .filter((item) => item.subItems);
      }
      return this.items;
    },
    items() {
      return [
        {
          title: this.$t("title.sms"),
          subItems: [
            {
              title: this.$t("title.single_sms_query"),
              url: "#api-SMS-GetSmsMessagesID",
            },
            {
              title: this.$t("title.sms_query"),
              url: "#api-SMS-GetSmsMessagesDirectionDirectionSent_atSent_at",
            },
            {
              title: this.$t("title.consult_report"),
              url: "#api-SMS-PostSmsInsights",
            },
            {
              title: this.$t("title.create_template"),
              url: "#api-SMS-PostSmsTemplates",
            },
            {
              title: this.$t("title.send_sms"),
              url: "#api-SMS-PostSmsMessages",
            },
            {
              title: this.$t("title.delete_template"),
              url: "#api-SMS-DeleteSmsTemplates",
            },
            {
              title: this.$t("title.list_templates"),
              url: "#api-SMS-GetSmsTemplates",
            },
          ],
        },
        {
          title: this.$t("title.whatsapp"),
          subItems: [
            {
              title: this.$t("title.consult_client"),
              url: "#api-Whatsapp-GetWhatsappClient",
            },
            {
              title: this.$t("title.create_client"),
              url: "#api-Whatsapp-PostWhatsappClient",
            },
            {
              title: this.$t("title.create_template"),
              url: "#api-Whatsapp-PostWhatsappTemplates",
            },
            {
              title: this.$t("title.send_message"),
              url: "#api-Whatsapp-PostWhatsappMessages",
            },
            {
              title: this.$t("title.delete_template"),
              url: "#api-Whatsapp-DeleteWhatsappTemplatesId",
            },
            {
              title: this.$t("title.list_messages"),
              url: "#api-Whatsapp-PostWhatsappMessagesList",
            },
            {
              title: this.$t("title.list_templates"),
              url: "#api-Whatsapp-GetWhatsappTemplatesList",
            },
          ],
        },
        {
          title: this.$t("title.rcs"),
          subItems: [
            {
              title: this.$t("title.send_message"),
              url: "#api-RCS-PostRcsMessages",
            },
            {
              title: this.$t("title.get_agent_capabilities"),
              url: "#api-RCS-GetRcsCapabilitiesPhone",
            },
            {
              title: this.$t("title.verify_rcs_capabilities"),
              url: "#api-RCS-PostRcsCapabilities",
            },
            {
              title: this.$t("title.list_templates"),
              url: "#api-RCS-GetRcsTemplates",
            },
            {
              title: this.$t("title.get_template"),
              url: "#api-RCS-GetRcsTemplate",
            },
            {
              title: this.$t("title.create_template"),
              url: "#api-RCS-PostRcsTemplate",
            },
            {
              title: this.$t("title.update_template"),
              url: "#api-RCS-PutRcsTemplate",
            },
          ],
        },
        {
          title: this.$t("title.email"),
          subItems: [
            {
              title: this.$t("title.consult_by_id"),
              url: "#api-E-mail-GetEmailMessagesId",
            },
            {
              title: this.$t("title.send_message"),
              url: "#api-E-mail-PostEmailMessages",
            },
            {
              title: this.$t("title.list_messages"),
              url: "#api-E-mail-GetEmailMessagesDirectionDirectionCreated_atCreated_at",
            },
          ],
        },
        {
          title: this.$t("title.webhooks"),
          subItems: [
            {
              title: this.$t("title.blacklist_optout"),
              url: "#api-Webhooks-PostSuaurl1",
            },
            {
              title: this.$t("title.send_callback"),
              url: "#api-Webhooks-PostSuaurl",
            },
          ],
        },
        {
          title: this.$t("title.lookup"),
          subItems: [
            {
              title: this.$t("title.lookup_number"),
              url: "#api-Lookup-GetLookupNumbersNumber",
            },
          ],
        },
        {
          title: this.$t("title.contacts"),
          subItems: [
            {
              title: this.$t("title.update_contact"),
              url: "#api-Contatos-PutContactsId",
            },
            {
              title: this.$t("title.create_contact"),
              url: "#api-Contatos-PostContacts",
            },
            {
              title: this.$t("title.delete_contact"),
              url: "#api-Contatos-DeleteContacts",
            },
            {
              title: this.$t("title.list_single_contact"),
              url: "api-Contatos-GetContactsId",
            },
            {
              title: this.$t("title.list_contacts"),
              url: "#api-Contatos-GetContacts",
            },
          ],
        },
        {
          title: this.$t("title.groups"),
          subItems: [
            {
              title: this.$t("title.create_group"),
              url: "#api-Grupos-PostContactsGroups",
            },
            {
              title: this.$t("title.delete_groups"),
              url: "#api-Grupos-DeleteContactsGroups",
            },
            {
              title: this.$t("title.delete_by_id"),
              url: "#api-Grupos-DeleteContactsGroupsId",
            },
            {
              title: this.$t("title.list_groups"),
              url: "#api-Grupos-GetContactsGroups",
            },
            {
              title: this.$t("title.search_by_id"),
              url: "#api-Grupos-GetContactsGroupsId",
            },
          ],
        },
        {
          title: this.$t("title.accounts"),
          subItems: [
            {
              title: this.$t("title.account_information"),
              url: "#api-Contas-GetAccountsInfo",
            },
          ],
        },
        {
          title: this.$t("title.users"),
          subItems: [
            {
              title: this.$t("title.invite_user"),
              url: "#api-Usuarios-PostUsersInvite",
            },
            {
              title: this.$t("title.delete_user"),
              url: "#api-Usuarios-DeleteUsers",
            },
            {
              title: this.$t("title.list_users"),
              url: "#api-Usuarios-GetUsers",
            },
          ],
        },
        {
          title: this.$t("title.keywords"),
          subItems: [
            {
              title: this.$t("title.update_by_id"),
              url: "#api-Palavras-Chave-PutKeywordsId",
            },
            {
              title: this.$t("title.create_keyword"),
              url: "#api-Palavras-Chave-PostKeywords",
            },
            {
              title: this.$t("title.delete_by_id"),
              url: "#api-Palavras-Chave-DeleteKeywordsId",
            },
            {
              title: this.$t("title.list_keywords"),
              url: "#api-Palavras-Chave-GetKeywords",
            },
          ],
        },
        {
          title: this.$t("title.optouts"),
          subItems: [
            {
              title: this.$t("title.update_number"),
              url: "#api-Optouts-PutOptoutsId",
            },
            {
              title: this.$t("title.create_optout"),
              url: "#api-Optouts-PostOptouts",
            },
            {
              title: this.$t("title.delete_by_id"),
              url: "#api-Optouts-DeleteOptoutsId",
            },
            {
              title: this.$t("title.delete_optouts"),
              url: "#api-Optouts-DeleteOptouts",
            },
            {
              title: this.$t("title.list_optouts"),
              url: "#api-Optouts-GetOptouts",
            },
          ],
        },
        {
          title: this.$t("title.blacklists"),
          subItems: [
            {
              title: this.$t("title.update_expiration"),
              url: "#api-Blacklists-PutBlacklistId",
            },
            {
              title: this.$t("title.create_blacklist"),
              url: "#api-Blacklists-PostBlacklist",
            },
            {
              title: this.$t("title.delete_blacklists"),
              url: "#api-Blacklists-DeleteBlacklist",
            },
            {
              title: this.$t("title.delete_by_id"),
              url: "#api-Blacklists-DeleteBlacklistId",
            },
            {
              title: this.$t("title.blacklists"),
              url: "#api-Blacklists-GetBlacklist",
            },
          ],
        },
      ];
    },
  },
  mounted() {
    this.setActive(this.$t("general.introduction"));
  },
  watch: {
    $i18n: {
      deep: true,
      handler() {
        this.activeItem = this.introduction;
      },
    },
  },
  methods: {
    clearSearch() {
      this.searchQuery = "";
    },
    handleMenuClick(item, index) {
      this.toggleSubmenu(index);
      this.setActive(item.title, index);
    },
    handleClick(url) {
      this.scrollToSection(url);
      this.toggleSidebar();
    },
    scrollToSection(id, title = null, index = null) {
      const elementId = id.replace("#", "");
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
          window.history.pushState(null, "", id);
          if (title) this.setActive(title, index);
        }
      }, 100);
    },
    toggleSubmenu(index) {
      const idx = this.openSubmenus.indexOf(index);
      if (idx !== -1) {
        this.openSubmenus.splice(idx, 1);
      } else {
        this.openSubmenus.push(index);
      }
    },
    isSubmenuOpen(index) {
      return this.openSubmenus.includes(index);
    },
    setActive(item, index) {
      this.activeItem = item;
      this.activeIndex = index;
    },
    isActive(item, index) {
      return this.activeItem === item && this.activeIndex === index;
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
  },
};
</script>

<style scoped>
.menu-icon {
  display: none;
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  top: 30px;
  left: 50px;
  z-index: 1000;
}

.sidebar {
  width: 250px;
  height: 100%;
  background-color: #f8f9fa;
  padding: 10px;
  border-right: 1px solid #e7e7e7;
  overflow-y: auto;
  position: fixed;
  transition: left 0.3s ease;
}

.sidebar-open {
  left: 0;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.search-bar input {
  flex-grow: 1;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 8px;
}

.search-bar button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  margin-left: 5px;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  color: black;
  padding: 5px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
}

.section-title:hover,
.section-title.active {
  background-color: var(--color-default);
  color: white;
}

nav ul {
  list-style: none;
  padding: 0;
  text-align: start;
}

nav li {
  padding: 5px 0px;
}

nav li ul li {
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 8px;
}

nav li ul li:hover {
  color: var(--color-default);
  background-color: #f1f0f0;
}

.img-logo {
  width: 65%;
  margin: 0.8rem 0 1.5rem 0;
  cursor: pointer;
}

.overlay {
  display: none;
}

@media (max-width: 960px) {
  .menu-icon {
    display: block;
  }

  .sidebar {
    width: 200px;
    height: 100vh;
    background-color: #f8f9fa;
    padding: 10px;
    border-right: 1px solid #e7e7e7;
    overflow-y: auto;
    position: fixed;
    top: 0;
    transition: left 0.25s ease;
    left: -250px;
  }

  .sidebar-open {
    left: 0;
  }

  .overlay {
    display: block;
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 220px;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
}
</style>
